import React, { useState, useRef } from 'react';
import { messagesAPI } from '../api';
import { Upload, Send, CheckCircle2, XCircle, Loader2, UserCheck, Download, Info } from 'lucide-react';
import * as XLSX from 'xlsx';

export default function AttendanceTab({ sessionId, schoolName }) {
  const [file, setFile] = useState(null);
  const [students, setStudents] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [sendMode, setSendMode] = useState('text');
  const fileInputRef = useRef(null);

  const handleFileSelect = (selectedFile) => {
    if (!selectedFile) return;
    setFile(selectedFile);
    setError('');
    setResult(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'array' });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
        const parsed = rows.map(row => ({
          name: String(row['Name'] || row['name'] || '').trim(),
          phone: String(row['Mobile No'] || row['Mobile Number'] || row['mobile_number'] || row['Phone'] || '').trim(),
          std: String(row['Std'] || row['std'] || row['Class'] || '').trim(),
          fromDate: String(row['From Date'] || row['from_date'] || '').trim(),
          toDate: String(row['To Date'] || row['to_date'] || '').trim(),
          totalDays: Number(row['Total Days'] || row['total_days'] || 0),
          presentDays: Number(row['Present Days'] || row['present_days'] || 0),
          absentDays: Number(row['Absent Days'] || row['absent_days'] || 0),
          holiday: Number(row['Holiday'] || row['holiday'] || row['Holidays'] || 0),
          remark: String(row['Remark'] || row['remark'] || '').trim(),
          month: String(row['Month'] || row['month'] || row['From Date'] || '').trim(),
        }));
        setStudents(parsed);
      } catch (err) {
        setError('Failed to parse file');
      }
    };
    reader.readAsArrayBuffer(selectedFile);
  };

  const handleDownloadTemplate = () => {
    const data = [
      {
        'Name': 'John Doe',
        'Mobile No': '919876543210',
        'Std': '10A',
        'From Date': '01/06/2024',
        'To Date': '30/06/2024',
        'Total Days': 26,
        'Present Days': 24,
        'Absent Days': 2,
        'Holiday': 0,
        'Remark': 'Good'
      },
      {
        'Name': 'Jane Smith',
        'Mobile No': '919876543211',
        'Std': '10B',
        'From Date': '01/06/2024',
        'To Date': '30/06/2024',
        'Total Days': 26,
        'Present Days': 20,
        'Absent Days': 5,
        'Holiday': 1,
        'Remark': 'Needs improvement'
      },
    ];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, 'Attendance');
    const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
    const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'attendance_template.xlsx'; a.click();
    URL.revokeObjectURL(url);
  };

  const handleSend = async () => {
    if (!file || students.length === 0) { setError('Please upload an Excel file first'); return; }
    setSending(true);
    setError('');
    setResult(null);

    let sent = 0, failed = 0;
    const results = [];

    for (const student of students) {
      if (!student.phone) { results.push({ name: student.name, status: 'skipped', reason: 'No phone' }); continue; }
      const pct = student.totalDays > 0 ? ((student.presentDays / student.totalDays) * 100).toFixed(1) : 0;
      const absentDays = student.absentDays || (student.totalDays - student.presentDays);
      const remark = student.remark || (parseFloat(pct) >= 75 ? 'Good attendance' : parseFloat(pct) >= 60 ? 'Needs improvement' : '⚠️ Poor attendance');
      const period = student.fromDate && student.toDate ? `${student.fromDate} to ${student.toDate}` : student.month;

      try {
        if (sendMode === 'text') {
          let msg = `Dear Parent of ${student.name},\nGreetings from ${schoolName || 'School'}\n\nAttendance Report Std: ${student.std || ''}`;
          msg += `\nFrom: ${student.fromDate || ''} to ${student.toDate || ''}`;
          msg += `\n\nPresent Days: ${student.presentDays}`;
          msg += `\nAbsent Days: ${absentDays}`;
          if (student.holiday > 0) msg += `\nHoliday: ${student.holiday}`;
          msg += `\nTotal Days: ${student.totalDays}`;
          msg += `\n\nPlease reply OK to confirm you have received this message.`;
          msg += `\n${schoolName || 'School'}`;
          await messagesAPI.sendManual({ sessionId, phone: student.phone, message: msg });
        } else if (sendMode === 'pdf' || sendMode === 'both') {
          // Generate PDF + send via send-marks endpoint (supports PDF)
          const formData = new FormData();
          // Create a mini Excel with this student for backend processing
          const wb = XLSX.utils.book_new();
          const ws = XLSX.utils.json_to_sheet([{
            'Name': student.name,
            'Mobile Number': student.phone,
            'Subject': 'Attendance - ' + (student.fromDate || student.month),
            'Test Date': student.fromDate || student.month,
            'To Date': student.toDate || '',
            'Std': student.std || '',
            'Total Marks': student.totalDays,
            'Obtained Marks': student.presentDays,
            'Highest Marks': student.totalDays,
            'Absent Days': student.absentDays || (student.totalDays - student.presentDays),
            'Remark': student.remark || '',
            'Obtained Marks': student.presentDays,
            'Highest Marks': student.totalDays,
          }]);
          XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
          const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
          const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
          formData.append('file', blob, 'attendance.xlsx');
          formData.append('sessionId', sessionId);
          formData.append('sendMode', sendMode === 'pdf' ? 'pdf' : 'both');
          formData.append('schoolName', schoolName || 'School');
          await messagesAPI.sendMarks(formData);
        }
        results.push({ name: student.name, status: 'sent' });
        sent++;
      } catch (err) {
        results.push({ name: student.name, status: 'failed', reason: err.response?.data?.error || err.message });
        failed++;
      }
      await new Promise(r => setTimeout(r, 700));
    }
    setResult({ total: students.length, sent, failed, results });
    setSending(false);
  };

  return (
    <div className="space-y-5">
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
        <Info size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-blue-800">
          Upload attendance data to send WhatsApp notifications to parents.
          Columns: <strong>Name, Mobile No, Std, From Date, To Date, Total Days, Present Days, Absent Days, Remark</strong>
        </p>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
        <h3 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <UserCheck size={18} className="text-blue-600" />
          Upload Attendance Excel
        </h3>
        <div
          className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
            dragActive ? 'border-blue-500 bg-blue-50' :
            file ? 'border-green-400 bg-green-50' :
            'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50'
          }`}
          onDrop={(e) => { e.preventDefault(); setDragActive(false); handleFileSelect(e.dataTransfer.files[0]); }}
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onClick={() => fileInputRef.current?.click()}
        >
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden"
            onChange={(e) => handleFileSelect(e.target.files[0])} />
          {file ? (
            <div className="flex flex-col items-center gap-2">
              <CheckCircle2 size={36} className="text-green-500" />
              <p className="font-semibold text-green-700">{file.name}</p>
              <p className="text-sm text-green-600">{students.length} students loaded</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3">
              <Upload size={36} className="text-gray-400" />
              <p className="font-semibold text-gray-600">Drag & drop attendance file</p>
              <button type="button" className="btn-3d-blue text-sm py-2 px-4"
                onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}>
                Choose File
              </button>
            </div>
          )}
        </div>

        <div className="mt-3">
          <button onClick={handleDownloadTemplate} className="btn-3d-blue text-sm py-2 px-4 flex items-center gap-2">
            <Download size={14} />
            Download Template
          </button>
        </div>
      </div>

      {/* Send Mode */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
        <h3 className="font-semibold text-gray-700 mb-3 text-sm">Send Mode</h3>
        <div className="grid grid-cols-3 gap-3">
          {[
            { id: 'text', label: 'Text Only', desc: 'WhatsApp text message', icon: '💬' },
            { id: 'pdf', label: 'PDF Only', desc: 'PDF attachment only', icon: '📄' },
            { id: 'both', label: 'Text + PDF', desc: 'PDF card + text message', icon: '📋' },
          ].map((mode) => (
            <button
              key={mode.id}
              onClick={() => setSendMode(mode.id)}
              className={`p-4 rounded-xl border-2 text-left transition-all ${
                sendMode === mode.id
                  ? 'border-blue-500 bg-blue-50 shadow-md'
                  : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/50'
              }`}
            >
              <div className="text-2xl mb-1">{mode.icon}</div>
              <div className={`font-semibold text-sm ${sendMode === mode.id ? 'text-blue-700' : 'text-gray-700'}`}>{mode.label}</div>
              <div className="text-xs text-gray-500 mt-0.5">{mode.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Message Template */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
          <span className="font-semibold text-gray-700 text-sm">Message Template</span>
          <span className="flex items-center gap-1 text-xs text-gray-400">
            <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/></svg>
            Default Attendance Format
          </span>
        </div>
        <div className="p-4">
          <textarea
            readOnly
            rows={10}
            value={`Dear Parent of {name},\nGreetings from {schoolName}\n\nAttendance Report Std: {std}\nFrom: {fromDate} to {toDate}\n\nPresent Days: {presentDays}\nAbsent Days: {absentDays}\nHoliday: {holiday}\nTotal Days: {totalDays}\n\nPlease reply OK to confirm you have received this message.\n{schoolName}`}
            className="w-full border rounded-lg p-3 text-sm font-mono resize-y bg-gray-50 border-gray-200 text-gray-500 cursor-not-allowed"
          />
          <p className="mt-1.5 text-xs text-gray-400">
            This template is auto-applied for attendance messages.
          </p>
        </div>
      </div>

      {/* Preview */}
      {students.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
            <span className="font-semibold text-gray-700 text-sm">Preview</span>
            <span className="text-xs bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full font-medium">{students.length} records</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  {['#','Name','Mobile No','Std','From','To','Total','Present','Absent','%','Remark'].map(h => (
                    <th key={h} className="px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase text-left">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {students.map((s, i) => {
                  const pct = s.totalDays > 0 ? ((s.presentDays / s.totalDays) * 100).toFixed(1) : 0;
                  const pctNum = parseFloat(pct);
                  const absentDays = s.absentDays || (s.totalDays - s.presentDays);
                  const remarkLabel = s.remark || (pctNum >= 75 ? 'Good' : pctNum >= 60 ? 'Average' : 'Poor');
                  const remarkColor = pctNum >= 75 ? 'text-green-600 bg-green-50' : pctNum >= 60 ? 'text-yellow-600 bg-yellow-50' : 'text-red-600 bg-red-50';
                  return (
                    <tr key={i} className={`border-b border-gray-100 hover:bg-blue-50 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                      <td className="px-3 py-2 text-xs text-gray-400">{i+1}</td>
                      <td className="px-3 py-2 font-medium text-gray-800">{s.name}</td>
                      <td className="px-3 py-2 text-gray-600 font-mono text-xs">{s.phone}</td>
                      <td className="px-3 py-2 text-gray-600 text-xs">{s.std}</td>
                      <td className="px-3 py-2 text-gray-600 text-xs">{s.fromDate}</td>
                      <td className="px-3 py-2 text-gray-600 text-xs">{s.toDate}</td>
                      <td className="px-3 py-2 text-center text-gray-700">{s.totalDays}</td>
                      <td className="px-3 py-2 text-center text-gray-700">{s.presentDays}</td>
                      <td className="px-3 py-2 text-center text-red-600 font-medium">{absentDays}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${pctNum >= 75 ? 'bg-green-50 text-green-700' : pctNum >= 60 ? 'bg-yellow-50 text-yellow-700' : 'bg-red-50 text-red-600'}`}>{pct}%</span>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${remarkColor}`}>{remarkLabel}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
          <XCircle size={18} className="text-red-500 flex-shrink-0" />
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {result && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 size={18} className="text-green-600" />
            <span className="font-semibold text-green-700">Sent {result.sent}/{result.total} messages</span>
          </div>
        </div>
      )}

      <button
        onClick={handleSend}
        disabled={sending || students.length === 0}
        className={`w-full py-4 rounded-xl font-bold text-white text-lg flex items-center justify-center gap-3 transition-all transform
          ${sending || students.length === 0
            ? 'bg-gray-300 cursor-not-allowed'
            : 'bg-orange-500 hover:bg-orange-600 shadow-[0_6px_0_#c2410c] active:shadow-[0_2px_0_#c2410c] active:translate-y-1'
          }`}
      >
        {sending ? <><Loader2 size={22} className="animate-spin" />Sending...</> : <><Send size={22} />Send Attendance Reports ({students.length})</>}
      </button>
    </div>
  );
}
